// ================================
// File: src/components/SEO/SeoMeta.jsx
// ================================

import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = 'Million2Trillion — The Verified Global Hub for the Financial Market Ecosystem';
const DEFAULT_DESCRIPTION = "Million2Trillion (M2T) is the verified global infrastructure platform for the financial market ecosystem—uniting traders, investors, coders, brokers, educators, and institutions into one powerful hub to learn, earn, connect, and grow.";
const DEFAULT_URL = 'https://million2trillion.com/';
const DEFAULT_IMAGE = '/assets/images/og-banner.png';
const DEFAULT_TWITTER = '@Million2Trillion';

export default function SeoMeta({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  url = DEFAULT_URL,
  image = DEFAULT_IMAGE,
  keywords,
  isBlogPost = false,
  datePublished = new Date().toISOString()
}) {
  const jsonLd = isBlogPost ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "headline": title,
    "description": description,
    "image": image.startsWith('http') ? image : `https://million2trillion.com${image}`,
    "author": {
      "@type": "Organization",
      "name": "Million2Trillion"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Million2Trillion",
      "logo": {
        "@type": "ImageObject",
        "url": "https://million2trillion.com/assets/images/logo.svg"
      }
    },
    "datePublished": datePublished,
    "dateModified": datePublished
  } : null;

  return (
    <Helmet>
      {/* Basic */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="A.G. Senthil Kumar, Founder of Million2Trillion" />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={isBlogPost ? "article" : "website"} />
      <meta property="og:site_name" content="Million2Trillion" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {isBlogPost && (
        <meta property="article:published_time" content={datePublished} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={DEFAULT_TWITTER} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Robots */}
      <meta name="robots" content="index, follow" />

      {/* Structured Data */}
      {isBlogPost && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
